import React, { useEffect, useMemo, useState } from "react";
import { Iconography } from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../redux";
import { getPartnerProgramBenefitsSagaAction } from "../../../../../redux/saga/partner-program/benefits/saga-actions";
import {
  BenefitItemType,
  BenefitTierType,
} from "../../../../../redux/slices/partner-program/benefits/Types";
import DescriptionModal from "../../../../hid-admin/dashboard/partner-organizations/manage-partner/partner-program/benefits/components/DescriptionModal";
import "../../../../hid-admin/dashboard/partner-organizations/manage-partner/partner-program/benefits/Benefits.scss";
import "./BenefitsComparison.scss";

const DELIVERY_BENEFITS_TYPE = "delivery";

const isTierActive = (
  benefit: BenefitItemType,
  tier: BenefitTierType,
  tierIndex: number
) =>
  benefit.activeTierIds.includes(tier.ID) ||
  // APIs may return 1-based positional tier IDs.
  benefit.activeTierIds.includes(tierIndex + 1);

const BenefitsComparison: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    categories,
    tierDetails,
    status: { benefitsLoading, benefitsErrorMessage },
  } = useAppSelector((state) => state.partner_program_benefits);

  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  const [selectedBenefit, setSelectedBenefit] =
    useState<BenefitItemType | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    dispatch(getPartnerProgramBenefitsSagaAction());
  }, [dispatch]);

  const visibleCategories = useMemo(
    () =>
      categories
        .map((category) => ({
          ...category,
          benefits: category.benefits.filter(
            (benefit) =>
              benefit.benefitsStatus === 1 &&
              (benefit.benefitsType || "").trim().toLowerCase() ===
                DELIVERY_BENEFITS_TYPE
          ),
        }))
        .filter((category) => category.benefits.length > 0),
    [categories]
  );

  const toggleCategory = (categoryId: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const renderContent = () => {
    if (benefitsLoading) {
      return <p className="pbc-message">Loading benefits...</p>;
    }
    if (benefitsErrorMessage) {
      return <p className="pbc-message pbc-message--error">{benefitsErrorMessage}</p>;
    }
    if (visibleCategories.length === 0) {
      return <p className="pbc-message">No benefits available for display.</p>;
    }

    return visibleCategories.map((category) => {
      const isCollapsed = collapsedIds.has(category.id);
      return (
        <section key={category.id} className="pbc-category">
          <button
            type="button"
            className="pbc-category-header"
            onClick={() => toggleCategory(category.id)}
            aria-expanded={!isCollapsed}
          >
            <span className={`pbc-chevron${isCollapsed ? " collapsed" : ""}`}>
              <Iconography icon="angleDown" />
            </span>
            <h3>{category.title}</h3>
          </button>

          {!isCollapsed && (
            <div className="pbc-table-card">
              <div className="pbc-table-scroll">
                <table className="pbc-table">
                  <thead>
                    <tr>
                      <th className="pbc-col-benefit">Benefits</th>
                      <th className="pbc-col-type">Benefit Type</th>
                      <th className="pbc-col-description">Description</th>
                      {tierDetails.map((tier) => (
                        <th key={tier.ID} className="pbc-col-tier">
                          {tier.tier_name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {category.benefits.map((benefit) => (
                      <tr key={benefit.id}>
                        <td className="pbc-col-benefit">{benefit.name}</td>
                        <td className="pbc-col-type">
                          {benefit.benefitsType || "-"}
                        </td>
                        <td className="pbc-col-description">
                          <button
                            type="button"
                            className="pbc-view-link"
                            onClick={() => setSelectedBenefit(benefit)}
                          >
                            View
                          </button>
                        </td>
                        {tierDetails.map((tier, tierIndex) => (
                          <td
                            key={`${benefit.id}-${tier.ID}`}
                            className="pbc-col-tier"
                          >
                            {isTierActive(benefit, tier, tierIndex) ? (
                              <span
                                className="pbc-tier-dot"
                                aria-label="Included"
                              />
                            ) : (
                              <span
                                className="pbc-tier-empty"
                                aria-label="Not included"
                              />
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      );
    });
  };

  return (
    <div className="partner-benefits-comparison">
      <div className="pbc-banner">
        <div className="pbc-banner-text">
          <h1>Benefit Comparison Table</h1>
          <p>Compare benefits across different tiers</p>
        </div>
        <button type="button" className="pbc-contact-btn" disabled>
          Need help? Contact us today
        </button>
      </div>

      <div className="pbc-categories">{renderContent()}</div>

      <DescriptionModal
        open={Boolean(selectedBenefit)}
        editable={false}
        title={selectedBenefit?.name || ""}
        value={selectedBenefit?.description || ""}
        onClose={() => setSelectedBenefit(null)}
        onSave={() => undefined}
      />
    </div>
  );
};

export default BenefitsComparison;
